<template>
  <div
    class="card h-100 border-0 open-game-mini"
    :class="isDarkMode ? 'open-game-mini-dark' : 'open-game-mini-light'"
    role="link"
    tabindex="0"
    @click="onCardClick"
    @keydown.enter="onCardClick"
  >
    <div class="mini-body">
      <div class="mini-photo">
        <img v-if="coverUrl" :src="coverUrl" :alt="gameName" />
        <span v-else class="mini-fallback">{{ monogram }}</span>
      </div>
      <div class="mini-copy min-w-0">
        <p class="mini-name text-truncate mb-0">{{ gameName }}</p>
        <p v-if="registerLabel" class="mini-line text-truncate mb-0">{{ registerLabel }}</p>
        <p v-if="prizeParts" class="mini-line text-truncate mb-0">
          <template v-if="prizeParts.kind === 'both'">
            <span class="mini-data">{{ prizeParts.count }}</span> {{ prizeParts.word }} v celkové hodnotě
            <span class="mini-data">{{ prizeParts.volume }}</span>
          </template>
          <template v-else-if="prizeParts.kind === 'volume'">
            v celkové hodnotě <span class="mini-data">{{ prizeParts.volume }}</span>
          </template>
          <template v-else>
            <span class="mini-data">{{ prizeParts.count }}</span> {{ prizeParts.word }}
          </template>
        </p>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed } from "vue";
import { useStore } from "vuex";

const props = defineProps({
  gameId: [String, Number],
  gameName: { type: String, default: "" },
  gameRegister: { type: String, default: "" },
  gameRewardCount: { type: [String, Number], default: 0 },
  gameRewardVolume: { type: [String, Number], default: 0 },
  gameRewardImg: { type: String, default: "" },
  gameRewardImgs: { type: Array, default: () => [] },
});

const store = useStore();
const isDarkMode = computed(() => !!store.state.isDarkMode);
const apiRoot = computed(() => process.env.VUE_APP_API_URL);

const rewardFileUrl = (file) => {
  const value = String(file || "").trim();
  if (!value) return "";
  if (value.startsWith("http")) return value;
  if (value.startsWith("img/")) return apiRoot.value + value;
  return apiRoot.value + "img/" + value;
};

const coverUrl = computed(() => {
  const extras = Array.isArray(props.gameRewardImgs) ? props.gameRewardImgs : [];
  const firstExtra = extras.map((item) => String(item || "").trim()).find(Boolean);
  return rewardFileUrl(firstExtra || props.gameRewardImg);
});

const monogram = computed(() => {
  const parts = String(props.gameName || "").trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return "H";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[1][0]).toUpperCase();
});

const registerLabel = computed(() => {
  const raw = String(props.gameRegister || "").trim();
  if (!raw) return "";
  const datePart = raw.split(/\s+/)[0] || "";
  const bits = datePart.split(".");
  if (bits.length < 2) return `Registrace do ${raw}`;
  const day = parseInt(bits[0], 10);
  const month = parseInt(bits[1], 10);
  const year = bits[2] ? parseInt(bits[2], 10) : null;
  if (!day || !month) return `Registrace do ${datePart}`;
  return year ? `Registrace do ${day}. ${month}. ${year}` : `Registrace do ${day}. ${month}.`;
});

const prizeCount = computed(() => parseInt(props.gameRewardCount, 10) || 0);

const prizeWord = computed(() => {
  const count = prizeCount.value;
  if (count === 1) return "výhra";
  if (count >= 2 && count <= 4) return "výhry";
  return "výher";
});

const prizeVolume = computed(() => {
  const amount = Number(props.gameRewardVolume);
  if (!Number.isFinite(amount) || amount <= 0) return "";
  return `${new Intl.NumberFormat("cs-CZ", {
    maximumFractionDigits: amount % 1 === 0 ? 0 : 2
  }).format(amount)} Kč`;
});

const prizeParts = computed(() => {
  const count = prizeCount.value;
  const volume = prizeVolume.value;
  const word = prizeWord.value;
  if (count && volume) return { kind: "both", count, word, volume };
  if (volume) return { kind: "volume", volume };
  if (count) return { kind: "count", count, word };
  return null;
});

const onCardClick = () => {
  if (!props.gameId) return;
  store.dispatch("loadOpenGameDetail", props.gameId);
};
</script>

<style scoped>
.open-game-mini {
  border-radius: 1rem !important;
  cursor: pointer;
  box-shadow: none !important;
  min-height: 92px;
}

.open-game-mini-light {
  background: #ffffff;
  border: 1px solid #E2E8F0 !important;
}

.open-game-mini-dark {
  background: #0F172A !important;
  border: 1px solid #334155 !important;
}

.mini-body {
  display: flex;
  align-items: center;
  gap: 0.85rem;
  padding: 0.75rem 0.95rem;
  min-height: 92px;
}

.mini-photo {
  width: 64px;
  height: 64px;
  border-radius: 0.75rem;
  overflow: hidden;
  flex-shrink: 0;
  background: #FFEDD5;
  display: flex;
  align-items: center;
  justify-content: center;
}

.open-game-mini-dark .mini-photo {
  background: #1E293B;
}

.mini-photo img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.mini-fallback {
  font-weight: 800;
  color: #FF6B21;
  font-size: 0.9rem;
}

.mini-copy {
  min-width: 0;
  flex: 1;
}

.mini-name {
  font-weight: 800;
  font-size: 0.95rem;
  line-height: 1.28;
  color: #0F172A;
}

.mini-line {
  margin-top: 0.28rem;
  font-size: 0.72rem;
  font-weight: 600;
  line-height: 1.3;
  color: #64748B;
}

.mini-data {
  font-weight: 800;
  color: #FF6B21;
}

.open-game-mini-dark .mini-name {
  color: #E2E8F0;
}

.open-game-mini-dark .mini-line {
  color: #94A3B8;
}

.open-game-mini-dark .mini-data {
  color: #FF8A4C;
}
</style>
